import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { proceduceService } from "../../../services/api/procedureService";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../components/Loading";
import { AuthContext } from "../../../contexts/AuthContext";
import { motion } from "framer-motion";
import MobilePatternHeader from "../components/MobilePatternHeader";

/* ------------ UI Helpers ------------ */

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200/70 ${className}`} />
);

const SectionTitle = ({ children }) => (
  <p className="font-sf-medium text-[14px] text-[#0D1829]">{children}</p>
);

/* ------------ Utils ------------ */
// Lấy tên file từ URL, decode và "làm đẹp" theo rule của bạn
const beautifyFileName = (url) => {
  if (!url) return "";
  try {
    const raw = url.split("/").pop() || "";
    // Nếu tên dạng "...-something-REALNAME.pdf" thì lấy phần sau dấu "-"
    const candidate = raw.includes("-") ? raw.split("-").pop() : raw;
    const decoded = decodeURIComponent(candidate);
    // Quy tắc bạn đang dùng: khoảng trắng -> "_"
    return decoded.replace(/\s+/g, "_");
  } catch {
    return url.split("/").pop();
  }
};

// Nếu server trả về text thuần (không phải HTML), hiển thị dạng <pre>
const isLikelyHtml = (text) => /<\/?[a-z][\s\S]*>/i.test(text);

/* ------------ Component ------------ */
const ProcedureDetail = ({ isMobile }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token_client } = useContext(AuthContext);

  const detailAbortRef = useRef(null);
  const txtAbortRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(false); // loading chi tiết
  const [txtLoading, setTxtLoading] = useState(false); // loading nội dung file
  const [data, setData] = useState({ name: "", txtUrl: "", fileUrl: "" });
  const [fileContent, setFileContent] = useState("");

  /* ------------ Fetch detail ------------ */
  const fetchProcedure = useCallback(async () => {
    try {
      // Abort request cũ nếu có
      try {
        detailAbortRef.current?.abort();
      } catch { }
      const controller = new AbortController();
      detailAbortRef.current = controller;

      setPageLoading(true);
      const response = await proceduceService.get_by_id(id, token_client, {
        signal: controller.signal,
      });

      const detail = response?.data || {};
      setData(detail);

      // fetch nội dung txt/html nếu có
      if (detail.txtUrl) {
        await fetchTextFile(detail.txtUrl, controller.signal);
      } else {
        setFileContent("");
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setPageLoading(false);
    }
  }, [id, token_client]);

  /* ------------ Fetch text/html content ------------ */
  const fetchTextFile = useCallback(async (content, parentSignal) => {
    // Kiểm tra xem content có phải là URL không
    const isUrl = /^https?:\/\//i.test(content);

    if (!isUrl) {
      // Nếu không phải URL, coi như là HTML content trực tiếp
      setFileContent(content);
      return;
    }

    // Nếu là URL thì fetch
    // Hủy request txt trước đó nếu có (độc lập với detail)
    try {
      txtAbortRef.current?.abort();
    } catch { }
    const controller = new AbortController();
    txtAbortRef.current = controller;

    // Nếu detail bị abort, abort luôn txt
    const onAbort = () => controller.abort();
    parentSignal?.addEventListener?.("abort", onAbort, { once: true });

    try {
      setTxtLoading(true);
      const res = await fetch(content, { signal: controller.signal });
      const text = await res.text();
      setFileContent(text || "");
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error("Lỗi khi tải file nội dung:", e);
        toast.dismiss();
      }
    } finally {
      setTxtLoading(false);
      parentSignal?.removeEventListener?.("abort", onAbort);
    }
  }, []);

  /* ------------ Download file ------------ */
  const download = useCallback((url) => {
    if (!url) {
      toast.dismiss();
      toast.error("Không có file để tải.");
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", url.split("/").pop());
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  /* ------------ Effects ------------ */
  useEffect(() => {
    fetchProcedure();
    return () => {
      try {
        detailAbortRef.current?.abort();
      } catch { }
      try {
        txtAbortRef.current?.abort();
      } catch { }
    };
  }, [fetchProcedure]);

  /* ------------ Render ------------ */
  if (pageLoading) {
    return (
      <div
        className={`flex ${isMobile ? "h-screen w-screen" : "h-full"
          } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  /* ------------ Mobile ------------ */
  if (isMobile) {
    return (
      <div className="h-full w-full">
        <MobilePatternHeader
          title="Chi tiết thủ tục"
          onBack={() => navigate(-1)}
        />

        <div className="h-[calc(100%-64px)] bg-[#F2F2F2] p-3">
          <motion.div
            className="overflow-auto max-h-[calc(100%-80px)] space-y-3 bg-white rounded-lg p-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
          >
            <h1 className="text-center font-sf-semibold text-[#0267FF] uppercase border-b pb-3">
              {data.name || "--"}
            </h1>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-[14px] text-[#2E3A59]">Loại dịch vụ:</p>
                <p className="font-sf-medium text-[14px] text-[#1A1A1A]">
                  {data?.categoryId?.categoryName || "--"}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-[14px] text-[#2E3A59]">
                  Thời gian nộp hồ sơ:
                </p>
                <p className="font-sf-medium text-[14px] text-[#1A1A1A]">
                  {data?.submissionTime || "--"}
                </p>
              </div>
              {data?.reviewTime && (
                <div className="flex items-center justify-between">
                  <p className="text-[14px] text-[#2E3A59]">
                    Thời gian thẩm định:
                  </p>
                  <p className="font-sf-medium text-[14px] text-[#1A1A1A]">
                    {data.reviewTime}
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <p className="font-sf-medium text-[14px] text-[#0D1829] pb-2 border-b">
                Nội dung
              </p>

              {/* Nội dung */}
              {txtLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-4/5" />
                  <Skeleton className="h-4 w-3/5" />
                  <Skeleton className="h-4 w-2/5" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              ) : fileContent ? (
                isLikelyHtml(fileContent) ? (
                  <div
                    className="
                  prose max-w-none
                  break-words
                  whitespace-normal
                  [&_*]:break-words
                  [&_*]:whitespace-normal
                  [&_img]:max-w-full [&_img]:h-auto
                  [&_table]:w-full [&_table]:block [&_table]:overflow-x-auto
                  [&_pre]:whitespace-pre-wrap [&_pre]:break-words
                  [&_code]:whitespace-pre-wrap [&_code]:break-words font-sf-regular
                "
                    dangerouslySetInnerHTML={{ __html: fileContent }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap break-words text-[14px] leading-6 text-[#0D1829]">
                    {fileContent}
                  </pre>
                )
              ) : (
                <p className="text-sm text-gray-500">
                  Không có nội dung hiển thị.
                </p>
              )}
            </div>

            {!!data.fileUrl && (
              <div className="flex flex-col gap-3">
                <SectionTitle>File thủ tục:</SectionTitle>
                <div key={data._id} className="flex gap-2 items-center">
                  <button
                    type="button"
                    className="w-full rounded-lg border border-[#EAEAEA] bg-white px-2 py-[13.5px] transition-colors hover:bg-[#f9f9f9]"
                    onClick={() => download(data.fileUrl)}
                    title="Tải xuống"
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <p className="text-gray-700 font-sf-medium text-[14px]">
                        {beautifyFileName(data.fileUrl)}
                      </p>
                      <div className="flex items-center gap-2">
                        <img
                          src="/images/pdf-icon.png"
                          alt=""
                          className="h-5 w-5"
                        />
                        <a
                          href={data.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0267FF] text-[12px] hover:text-blue-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Mở file PDF
                        </a>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  /* ------------ Desktop ------------ */
  return (
    <div className="h-full overflow-auto">
      <motion.div
        className="h-full bg-white rounded-[16px] border border-[#D2D5DA] p-4 flex flex-col gap-4"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
      >
        <h1 className="text-center border-b p-4 font-sf-semibold uppercase text-2xl text-[#0457C2]">
          {data.name || "--"}
        </h1>

        <div className="flex h-[calc(100%-65px)] flex-col gap-6 overflow-auto px-[5vw] pt-6">
          {/* Thông tin tổng quan */}
          <div className="flex flex-wrap gap-4">
            <div className="flex gap-2">
              <p className="font-sf-semibold">Loại dịch vụ:</p>
              <p>{data?.categoryId?.categoryName || "--"}</p>
            </div>
            {data?.submissionTime && (
              <div className="flex gap-2">
                <p className="font-sf-semibold">Thời gian nộp hồ sơ:</p>
                <p>{data.submissionTime}</p>
              </div>
            )}
            {data?.reviewTime && (
              <div className="flex gap-2">
                <p className="font-sf-semibold">Thời gian thẩm định:</p>
                <p>{data.reviewTime}</p>
              </div>
            )}
          </div>

          {/* Nội dung */}
          <div className="flex flex-col gap-3">
            <p className="font-sf-semibold">Nội dung:</p>

            {txtLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/5" />
                <Skeleton className="h-4 w-2/5" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            ) : fileContent ? (
              isLikelyHtml(fileContent) ? (
                <div
                  className="
                  prose max-w-none
                  break-words
                  whitespace-normal
                  [&_*]:break-words
                  [&_*]:whitespace-normal
                  [&_img]:max-w-full [&_img]:h-auto
                  [&_table]:w-full [&_table]:block [&_table]:overflow-x-auto
                  [&_pre]:whitespace-pre-wrap [&_pre]:break-words
                  [&_code]:whitespace-pre-wrap [&_code]:break-words font-sf-regular
                "
                  dangerouslySetInnerHTML={{ __html: fileContent }}
                />
              ) : (
                <pre className="whitespace-pre-wrap break-words text-[15px] leading-7">
                  {fileContent}
                </pre>
              )
            ) : (
              <p className="text-sm text-gray-500">
                Không có nội dung hiển thị.
              </p>
            )}
          </div>

          {/* File đính kèm */}
          {!!data.fileUrl && (
            <>
              <div className="flex flex-col gap-3">
                <p className="font-sf-semibold">File thủ tục:</p>
                <div key={data._id} className="flex items-center gap-2">
                  <button
                    type="button"
                    className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-[#EAEAEA] bg-white px-5 py-[13.5px] transition-colors hover:bg-[#f9f9f9]"
                    onClick={() => download(data.fileUrl)}
                    title="Tải xuống"
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="/images/document.png"
                        alt=""
                        className="h-5 w-5"
                      />
                      <p className="text-sm font-sf-medium uppercase text-gray-700">
                        {beautifyFileName(data.fileUrl)}
                      </p>
                    </div>
                    <a
                      href={data.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#0267FF] hover:text-blue-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Mở file
                    </a>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ProcedureDetail;
